/* eslint-disable max-lines */
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import { LocalizationEdit } from '@modules/_shared/components/LocalizationEdit'
import { TradeTag } from '@modules/_shared/components/TradeTag'
import {
  getTaskInputFromTaskWithoutStartRows,
  updateTaskByStartDateAndRequiredDuration,
} from '@modules/_shared/helpers/task.helpers'

import { useModal } from '@hooks/app/modal.hook'
import { useCreateComment } from '@hooks/comment/create-comment.hook'
import { useFilePreviews } from '@hooks/file/file-preview.hook'
import { useTaskWithComments } from '@hooks/planning/task-with-comments.hook'
import { useUpdateTaskStatus } from '@hooks/planning/update-task-status.hook'
import { useUpdateTask } from '@hooks/planning/update-task.hook'
import { useProjectGuards } from '@hooks/project/project-guards.hook'
import { useResolveZone } from '@hooks/zone/resolve-zone.hook'
import { useZoning } from '@hooks/zone/zoning.hook'
import { useUpdateTaskProgress } from '@hooks/planning/update-task-progress.hook'

import { useIOSPreventAutoZooming } from '@utils/hooks/useIOSPreventZooming'

import { CommentableType, ProjectFilePosition, RemoteFile, Task, TaskStatus } from '@types'

import { Modal } from '@components/Modal'
import { SendProps, WriteComment } from '@components/WriteComment'

import { TaskDetails } from '../../components/TaskDetails'

import { TaskData, TaskEdit } from '../../../project-planning/components/TaskEdit'

import { AddCommentContainer, Container, LabelCompany, SubTitleContainer } from './mobile.edit-task.styled'

type Mode = 'details' | 'edit' | 'localization'

interface TaskTitles {
  details: string
  edit: string
  localization: string
}

interface TaskActions {
  details?: string
  edit: string
  localization?: string
}

export const MobileEditTaskPage: React.FC = () => {
  const { t } = useTranslation()
  const { hideModal } = useModal()
  const { projectId } = useParams()
  const [searchParams] = useSearchParams()
  const taskId = searchParams.get('taskId')

  useIOSPreventAutoZooming()

  const [mode, setMode] = useState<Mode>('details')
  const [taskMode, setTaskMode] = useState<'form' | 'localization'>('form')
  const [planFile, setPlanFile] = useState<RemoteFile>()

  const [taskData, setTaskData] = useState<TaskData>({
    name: '',
    startAt: undefined,
    requiredDuration: undefined,
    workforce: undefined,
    status: undefined,
    zone: undefined,
    company: undefined,
    trade: undefined,
    projectFile: undefined,
    projectFilePosition: undefined,
  })

  const { getProjectGuards } = useProjectGuards({ projectId })
  const { canCommentTask, canEditTaskStatus, canEditTask } = getProjectGuards()
  const { resolveZone } = useResolveZone()

  const { updateTaskStatus } = useUpdateTaskStatus()
  const { updateTaskProgress } = useUpdateTaskProgress()
  const { createComment } = useCreateComment()
  const { updateTask } = useUpdateTask()

  const zoningQuery = useZoning(projectId || '')
  const taskQuery = useTaskWithComments(taskId || '')

  const task = taskQuery.data?.task
  const zoning = zoningQuery.data?.zoning || []

  const isLoadingZoning = zoningQuery.loading ? 1 : zoningQuery.error ? -1 : 0
  const isLoadingTask = taskQuery.loading ? 1 : taskQuery.error ? -1 : 0

  const refScrollView = useRef<HTMLDivElement | null>(null)

  const { building, floor, zone } = resolveZone(zoning, task?.zoneId)

  const { loadFilePreviews: loadPlanPreview } = useFilePreviews({
    files: taskData?.projectFile?.file ? ([taskData?.projectFile?.file] as RemoteFile[]) : undefined,
    onFilesLoaded: ([file]) => setPlanFile(file as RemoteFile),
  })

  const titles: TaskTitles = {
    details: task?.idNumber ? `Tâche #${task?.idNumber} - ${task?.name}` : '',
    edit: t('modules_project-suivi_pages_edit-task_title'),
    localization: t('modules_project-suivi_pages_edit-task_localization_title'),
  }

  const submitLabels: TaskActions = {
    details: undefined,
    edit: t('modules_project-suivi_pages_edit-task_submit_edit'),
    localization: undefined,
  }

  useEffect(() => {
    if (task) {
      setTaskData({
        name: task.name,
        startAt: task.startAt,
        requiredDuration: task.requiredDuration,
        workforce: task.workforce,
        status: task.status,
        zone: task.zone,
        company: task.company,
        trade: task.trade,
        projectFile: task.projectFile,
        projectFilePosition: task.projectFilePositions ? task.projectFilePositions[0] : undefined,
      })
    }
  }, [task])

  useEffect(() => {
    if (taskData?.projectFile) {
      loadPlanPreview()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.projectFile, taskData.projectFile])

  useEffect(() => {
    if (task?.comments && task.comments.length > (taskQuery.data?.task?.comments.length || 0)) {
      refScrollView.current?.scrollTo({ top: refScrollView.current.scrollHeight, behavior: 'smooth' })
    }
  }, [task?.comments, taskQuery.data?.task?.comments.length])

  const onStatusChange = (newStatus?: TaskStatus): void => {
    if (newStatus && taskId) {
      setTaskData((prev) => ({ ...prev, status: newStatus }))
      updateTaskStatus({ taskId, status: newStatus })
    }
  }

  const onProgressChange = (progress: number): void => {
    if (taskId) {
      updateTaskProgress({ taskId, progress })
      taskQuery.refetch(taskId)
    }
  }

  const handleSubmit = () => {
    if (!taskId || !task) return

    if (taskMode === 'localization') {
      setTaskMode('form')
      return
    }

    const taskToEdit: Task = {
      ...task,
      name: taskData.name,
      status: taskData.status!,
      startAt: taskData.startAt!,
      duration: taskData.requiredDuration!,
      requiredDuration: taskData.requiredDuration!,
      workforce: taskData.workforce!,
      zoneId: taskData.zone?.id || task.zoneId,
      zone: taskData.zone,
      companyId: taskData.company?.id,
      company: taskData.company,
      tradeKey: taskData.trade!.key,
      trade: taskData.trade!,
      projectFileId: taskData.projectFile?.id || task.projectFileId,
      projectFile: taskData.projectFile,
      projectFilePositions: taskData.projectFilePosition
        ? [
            {
              x: taskData.projectFilePosition.x,
              y: taskData.projectFilePosition.y,
            },
          ]
        : undefined,
    }

    const updatedTask = updateTaskByStartDateAndRequiredDuration(
      taskToEdit,
      taskToEdit.startAt,
      taskToEdit.requiredDuration,
    )

    updateTask({
      taskId: taskId,
      task: getTaskInputFromTaskWithoutStartRows(updatedTask),
    })

    if (mode === 'edit' || mode === 'localization') {
      setMode('details')
    }
  }

  const handleOnPrevious = () => {
    if (taskMode === 'localization') {
      setTaskMode('form')
      return
    }

    if (mode === 'edit') {
      if (task) {
        setTaskData({
          name: task.name,
          trade: task.trade,
          company: task.company,
          requiredDuration: task.requiredDuration,
          workforce: task.workforce,
          startAt: task.startAt,
          status: task.status,
          zone: task.zone,
          zoneId: task.zoneId,
          projectFile: task.projectFile,
          projectFilePosition: task.projectFilePositions ? task.projectFilePositions[0] : undefined,
        })
      }
      setMode('details')
    }

    if (mode === 'localization') {
      handleSubmit()
    }
  }

  const showFooter = () => {
    if (taskMode === 'localization') return false

    const footerRules: {
      [key: string]: () => boolean
    } = {
      details: () => canCommentTask,
      edit: () => canEditTask,
    }

    const rule = footerRules[mode]
    return rule ? rule() : true
  }

  const onSubmit = () => {
    if (!projectId || !taskId) return

    if (taskMode === 'localization') {
      setTaskMode('form')
      return
    }

    if (mode === 'edit') {
      handleSubmit()
    }
  }

  const renderSubtitle = () => {
    if (!task || mode !== 'details') return undefined

    return (
      <SubTitleContainer>
        <TradeTag trade={task.trade} />
        <LabelCompany>{task.company?.name}</LabelCompany>
      </SubTitleContainer>
    )
  }

  const handleCommentSubmit = async ({ value, files, reset, setLoading }: SendProps) => {
    if (!taskId) return

    setLoading(true)
    try {
      await createComment({ commentableId: taskId, commentableType: CommentableType.Task, value }, files[0])
      reset()
    } catch (error) {
      console.error('Error creating comment:', error)
    }
    setLoading(false)
  }

  const renderFooter = () => {
    if (mode !== 'details') return undefined

    return (
      <AddCommentContainer>
        <WriteComment onClickSend={handleCommentSubmit} />
      </AddCommentContainer>
    )
  }

  return (
    <Modal
      refScrollContainer={(ref) => (refScrollView.current = ref)}
      title={titles[mode]}
      onSubmitLabel={submitLabels[mode]}
      showPrevious={mode !== 'details'}
      onSubmit={onSubmit}
      onPrevious={handleOnPrevious}
      onClose={() => hideModal('update_task', { taskId })}
      showClose={taskMode !== 'localization' && mode !== 'localization'}
      SubTitleComponent={renderSubtitle()}
      FooterComponent={renderFooter()}
      showFooter={showFooter()}
      autoHideOnSubmit={false}
    >
      <Container id="container">
        {!task ? null : (
          <>
            {mode === 'details' && (
              <TaskDetails
                task={task}
                loading={isLoadingZoning || isLoadingTask}
                building={building}
                floor={floor}
                zone={zone}
                canEditTask={canEditTask}
                canEditTaskStatus={canEditTaskStatus}
                onRefresh={() => {
                  zoningQuery.refetch(projectId || '')
                  taskQuery.refetch(taskId || '')
                }}
                onStatusChange={onStatusChange}
                onProgressChange={onProgressChange}
                onEditClick={() => setMode('edit')}
                onLocalizationClick={() => setMode('localization')}
              />
            )}

            {mode === 'edit' && (
              <TaskEdit
                projectId={projectId || ''}
                task={task}
                planFile={planFile}
                taskData={taskData}
                onTaskChange={setTaskData}
                mode={taskMode}
                disableSearches
                disabledAddCompany
                onAddLocalization={() => setTaskMode('localization')}
              />
            )}

            {mode === 'localization' && (
              <LocalizationEdit
                markerType="task"
                file={planFile}
                projectFile={taskData.projectFile}
                projectId={projectId || ''}
                data={task}
                onAdd={(projectFilePosition: ProjectFilePosition) => setTaskData({ ...taskData, projectFilePosition })}
                onRemove={() => setTaskData({ ...taskData, projectFilePosition: undefined })}
                onChange={(projectFilePosition: ProjectFilePosition) =>
                  setTaskData({ ...taskData, projectFilePosition })
                }
                onChangeProjectFile={(_, projectFile) => {
                  setTaskData((prev) => ({
                    ...prev,
                    projectFile,
                    projectFilePosition: projectFile ? prev.projectFilePosition : undefined,
                  }))
                }}
              />
            )}
          </>
        )}
      </Container>
    </Modal>
  )
}
